import React, { useEffect, useState, useRef, useMemo, useContext } from "react";
import { Col, Row, Button, CardBody, Card, Input, Label } from "reactstrap";
import _ from "lodash";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { AttachmentManager, CustomerTypeahead, ServiceThreadSpinner } from "../";
import { api, constants, helpers, dateHelpers, UserContext } from "../../utils";
import { DesktopContainer } from "../../layouts";
import axios from "axios";
import { toast } from "react-toastify";
import DebounceInput from "react-debounce-input";

const blankForm = {
   action: "",
   actionId: 951,
   actionType: 1,
   attachmentType: 10,
   caseNumber: "", // -- multi
   completedBy: "",
   completionDate: null,
   createdDate: "2023-01-23",
   customerId: "",
   customerIdName: "",
   displayId: "CAR2023-951",
   followUp: "",
   followUpBy: "",
   followUpDate: null,
   invoiceNumber: "", // -- invoices
   originator: "Aaron Denton",
   problemDescription: "",
   problemTypeDescription: "",
   problemTypeId: 0,
   productType: "", // -- customer part number
   productionOperator: "",
   quantity: 0,
   rootCause: "",
   source: null,
   status: 1,
   statusDescription: "Open",
   targetDate: "2023-01-30",
   targetFollowUpDate: "2023-02-22",
   unitOfMeasure: "",
   customerIdSelect: null,
   invoiceNumberSelect: null,
   productTypeSelect: null,
   caseNumberSelect: null,
   problemTypeIdSelect: null,
   followUpBySelect: null,
};
const selectListInit = {
   invoices: [],
   partNumbers: [],
   caseNumbers: [],
};
const status = {
   Open: 1,
   Closed: { label: "Investigation Completed", value: 2 },
   Canceled: { label: "Investigation Canceled", value: 5 },
};

function ColPrompt({ title }) {
   return (
      <Col xs="3" className="text-white">
         <h5 style={{ textAlign: "right" }}>{title}:</h5>
      </Col>
   );
}

function RadioOption({ onChange, currentValue, classType, fieldName }) {
   return (
      <Col xs="3" className="text-white">
         <Label>
            <Input
               type="radio"
               name={fieldName}
               onChange={onChange}
               onClick={onChange}
               defaultValue={currentValue === classType.value}
               checked={currentValue === classType.value}
            />
         </Label>
         <span className="ms-2">
            {classType.label ? classType.label : classType}
         </span>
      </Col>
   );
}

export default function CorrectiveActionEdit(props) {
   const { actionId } = useParams();
   const cancelToken = axios.CancelToken;
   const firstRender = useRef({
      invoices: true,
      partNumbers: true,
      caseNumbers: true,
      problemTypes: true,
      followByUsers: true,
   });
   const location = useLocation();
   const navigate = useNavigate();
   const source = cancelToken.source();
   const userCtx = useContext(UserContext);
   const [currentActionDto, setCurrentActionDto] = useState(blankForm);
   const [currentCustomer, setCurrentCustomer] = useState(null);
   const [currentInvoices, setCurrentInvoices] = useState(null);
   const [currentPart, setCurrentPart] = useState(null);
   const [isDisabled, setIsDisabled] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [itemUofM, setItemUofM] = useState("");
   const [selectList, setSelectList] = useState(selectListInit);
   const [selectLoading, setSelectLoading] = useState(false);
   const [staticSelectList, setStaticSelectList] = useState({
      problemTypes: [],
      followByUsers: [],
   });
   const { customerIdSelect, invoiceNumberSelect, productTypeSelect } = useMemo(
      () => currentActionDto,
      [currentActionDto]
   );

   useEffect(() => {
      if (!_.isNil(actionId)) {
         setIsLoading(true);
         setSelectLoading(true);
         api
            .postS(
               "Quality/FetchCorrectiveAction",
               { actionId: actionId },
               source.Token
            )
            .then((r) => {
               if (r.data.success) {
                  let coa = r.data.correctiveAction;
                  if (
                     coa.status !== status.Open &&
                     userCtx &&
                     userCtx.currentUser &&
                     !userCtx.currentUser.isAdmin
                  )
                     setIsDisabled(true);
                  if (coa.customerId !== "")
                     coa.customerIdSelect = {
                        label: coa.customerIdName,
                        value: coa.customerId,
                     };
                  if (coa.customerIdSelect) {
                     setCurrentCustomer(coa.customerIdSelect);
                  }
                  setCurrentActionDto({
                     ...currentActionDto,
                     ...coa,
                     targetDate: dateHelpers.formatDateForServer(coa.targetDate),
                     targetFollowUpDate: dateHelpers.formatDateForServer(
                        coa.targetFollowUpDate
                     ),
                  });
                  setSelectList({
                     invoices: coa.invoices,
                     partNumbers: coa.partNumbers,
                     caseNumbers: coa.caseNumbers,
                  });
                  setStaticSelectList({
                     problemTypes: coa.problemTypes.map(item => item.active === false ? { ...item, isDisabled: true } : item),
                     followByUsers: coa.followByUsers,
                  });
               } else {
                  toast.error(r.data.message);
               }
            })
            .catch(api.catchHandler)
            .finally(() => {
               setIsLoading(false);
               setSelectLoading(false);
            });
      } else {
         console.error("actionId is not here");
      }

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [actionId, location]);

   useEffect(() => {
      if (
         currentActionDto &&
         currentActionDto.productTypeSelect &&
         currentActionDto.customerIdSelect
      ) {
         getItemUofM();
      } else {
         setItemUofM("");
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [currentActionDto.productTypeSelect]);

   //Updates When You Select a Customer, if null,
   //everything is set to null, if selection is cleared,
   //if customer changes load fresh invoices and set other selects to empty,
   //if customer remains the same do not call api again
   useEffect(() => {
      if (selectLoading) return;
      let copy = _.cloneDeep(currentActionDto); //copy state
      if (firstRender.current.invoices) {
         firstRender.current.invoices = false;
         return;
      }
      if (_.isEqual(currentCustomer, copy.customerIdSelect)) {
         //check to make sure we don't call the same api if nothing has changed
         //both are null on initial load
         return;
      } else {
         setCurrentCustomer(copy?.customerIdSelect);
         setSelectList(selectListInit);
         setCurrentActionDto({
            ...copy,
            invoiceNumberSelect: [],
            productTypeSelect: null,
            caseNumberSelect: [],
         });
         //assign current customer to check against on future re renders
      }
      if (
         _.isNil(copy?.customerIdSelect) ||
         copy?.customerIdSelect?.value === (0 || -1)
      ) {
         setSelectList(selectListInit);
         setCurrentActionDto({
            ...copy,
            invoiceNumberSelect: [],
            productTypeSelect: null,
            caseNumberSelect: [],
         });
         return;
      } //Silently just return if nothing is selected no hassle
      setSelectLoading(true);
      api
         .post("Quality/FetchInvoices", {
            customerId: copy.customerIdSelect.value,
         })
         .then((r) => {
            let box = [];
            if (r.data.success) {
               _.map(r.data.list, (x) => {
                  if (!_.includes(box, x.orderId)) {
                     box.push(x.orderId);
                  }
               });
               const listOfInv = _.map(box, (x) => {
                  return { label: x, value: x };
               });

               listOfInv.push({ label: "unknown", value: "unknown" });
               setSelectList({
                  invoices: listOfInv,
                  partNumbers: [],
                  caseNumbers: [],
               });
            } else {
               toast.error(`Could not render the invoice list`);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setSelectLoading(false));

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [customerIdSelect]);

   useEffect(() => {
      if (selectLoading) return;
      let copy = _.cloneDeep(currentActionDto);
      if (_.isEqual(currentInvoices, copy.invoiceNumberSelect)) return; //check to make sure we don't call the same api if nothing has changed also guards init
      setCurrentInvoices(copy?.invoiceNumberSelect); //assign current customer to check against on future re renders
      if (
         _.isNil(copy?.invoiceNumberSelect) ||
         copy.invoiceNumberSelect.length === 0 ||
         _.isNil(copy?.customerIdSelect) ||
         copy?.customerIdSelect?.value === (0 || -1)
      ) {
         setSelectList({
            ...selectList,
            partNumbers: [],
            caseNumbers: [],
         });
         setCurrentActionDto({
            ...copy,
            productTypeSelect: null,
            caseNumberSelect: null,
         });
         return;
      } //Silently just return if nothing is selected no hassle
      let invoiceString = "";
      if (copy.invoiceNumberSelect.length)
         invoiceString = getCommaDelimitedString(copy.invoiceNumberSelect);
      let searchAll = false;
      if (_.includes(invoiceString, "unknown")) searchAll = true;
      setSelectLoading(true);
      api
         .postS(
            "Quality/FetchPartNumbers",
            {
               InvoicesDelimited: invoiceString,
               CustomerId: copy.customerIdSelect.value,
               Unknown: searchAll,
            },
            source.token
         )
         .then((r) => {
            if (r.data.success) {
               const uniqResults = _.uniqBy(r.data.list, (x) => x.label);

               const trimmedUniqResults = _.map(uniqResults, (x) => {
                  x.value = _.trim(x.value);
                  return x;
               });
               setSelectList({
                  ...selectList,
                  partNumbers: trimmedUniqResults,
                  caseNumbers: [],
               });
            } else {
               if (axios.isCancel(r)) {
                  console.info("axios request cancelled");
                  return;
               }
               toast.error(`Could not render the partNumber list`);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setSelectLoading(false));

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [invoiceNumberSelect]);

   useEffect(() => {
      let copy = _.cloneDeep(currentActionDto);
      if (_.isEqual(currentPart, copy?.productTypeSelect)) return;
      setCurrentPart(copy?.productTypeSelect);
      if (_.isNil(copy?.productTypeSelect)) {
         setSelectList({
            ...selectList,
            caseNumbers: [],
         });
         setCurrentActionDto({
            ...copy,
            caseNumberSelect: null,
         });
         return;
      }
      let invoiceString = "";
      if (copy.invoiceNumberSelect.length)
         invoiceString = getCommaDelimitedString(copy.invoiceNumberSelect);
      setSelectLoading(true);
      api
         .postS(
            "Quality/FetchCaseNumbers",
            {
               ItemNumber: copy.productTypeSelect.value,
               InvoicesDelimited: invoiceString,
            },
            source.token
         )
         .then((r) => {
            if (r.data.success) {
               let caseNumFromAPI = r.data.list;
               _.map(caseNumFromAPI, (x) => {
                  x.value = _.trim(x.value);
                  x.label = _.trim(x.label);
                  return x;
               });
               setSelectList({
                  ...selectList,
                  caseNumbers: caseNumFromAPI,
               });
            } else {
               if (axios.isCancel(r)) {
                  console.info("axios request cancelled");
                  return;
               }
               toast.error(r.data.message);
            }
         })
         .catch(api.catchHandler)
         .finally(() => setSelectLoading(false));

      return () => {
         source.cancel("axios request cancelled");
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [productTypeSelect]);

   const getCommaDelimitedString = (list) => {
      let result = "";
      _.map(list, (x) => {
         if (typeof x.value === "string") {
            result += `${x.value.trim()},`;
         }
      });
      result = result.slice(0, -1);
      return result;
   };

   function getItemUofM() {
      // escape the forward slash character or pound symbol if in the item so it won't mess up the URL
      // Example = MP.11/64S40007 this causes the url to look like it has an extra parameter because
      // of the / character so the URL is broken and a Not Found error is thrown.
      var encodedItem = currentActionDto.productTypeSelect.value.replace(/\//g, '%2F').replace(/#/g, '%23');
      api
         .fetch(
            `Item/GetItemUofM/${encodedItem}/${currentActionDto.customerIdSelect.value}`
         )
         .then((response) => {
            if (response.data && response.data.success) {
               setItemUofM(response.data.message);
            } else {
               setItemUofM("");
            }
         })
         .catch((error) => {
            api.catchHandler(error);
         });
   }

   const onChange = (property, value, secProp = null, secValue = null) => {
      let copy = _.cloneDeep(currentActionDto);
      if (!_.isNil(secProp) && !_.isNil(secValue)) _.set(copy, secProp, secValue);
      _.set(copy, property, value);
      setCurrentActionDto(copy);
   };

   const onChangeStatus = (property, value) => {
      if (value === currentActionDto.status) value = 1;
      let copy = _.cloneDeep(currentActionDto);
      _.set(copy, property, value);
      setCurrentActionDto(copy);
      if (value === 2 && currentActionDto.completionDate === null) {
         currentActionDto.completionDate = dateHelpers.formatDateForServer(dateHelpers.currentDate());
      }
   };

   const returnCustomerSelect = (customerIdSelect) => {
      if (!_.isNil(customerIdSelect)) return customerIdSelect;
      // how do we tell from a null when they clear it and a null when it comes back null or empty
      return { label: "--- Select ---", value: 0 };
   };

   const returnInvoiceTypeSelect = (invoices, select) => {
      if (!_.isNil(select)) return select;
      if (invoices === "") return currentActionDto.invoiceNumberSelect;
   };

   const runReport = () => {
      //send the primary key action Id and the action Type to decide which report to retieve
      setIsLoading(true);
      api
         .postDownloadBinary(
            "Report/CorrectiveAction",
            {
               actionType: currentActionDto.actionType,
               actionId: currentActionDto.actionId,
            },
            constants.MIME_TYPES.pdf,
            currentActionDto.displayId + ".pdf",
            "There was an error downloading this corrective action pdf"
         )
         .then((res) => {
            if (res?.data === false) {
               toast.error(res.message);
            }
         })
         .catch((error) => {
            api.catchHandler(error);
         })
         .finally(() => setIsLoading(false));
   };

   const SaveCorrectiveAction = () => {
      let copy = _.cloneDeep(currentActionDto);
      let payload = {};
      for (let key in copy) {
         let value = copy[key];
         if (_.includes(key, "Selec")) {
            let newKey = key.slice(0, key.length - 6);
            let newValue = value;
            if (_.isArray(value) && value.length) {
               newValue = getCommaDelimitedString(value);
            } else {
               newValue = helpers.valueFromSelect(value);
               newValue = _.trim(newValue);
            }
            Object.assign(payload, { [newKey]: newValue });
         } else {
            Object.assign(payload, { [key]: value });
         }
      }
      const validated = ValidatePayload(payload);
      if (validated) {
         api
            .post("Quality/SaveCorrectiveAction", payload)
            .then((r) => {
               if (r.data.success) {
                  navigate(0);
                  toast.info("Successfully updated");
               } else {
                  toast.error("Something isn't right... Please try again later");
                  throw new Error(r.data.message);
               }
            })
            .catch(api.catchHandler);
      }
   };

   const ValidatePayload = (payload) => {
      let errors = "";
      if (payload.quantity === "") payload.quantity = 0;
      if (payload.problemTypeId === "") payload.problemTypeId = 0;
      if (payload.status === "") payload.status = 1;

      if (typeof payload.quantity === "string")
         payload.quantity = parseInt(payload.quantity ?? "0");
      if (typeof payload.problemTypeId === "string")
         payload.problemTypeId = parseInt(payload.problemTypeId ?? "0");

      if (payload.problemTypeId === 0)
         errors = errors.concat("<br />Problem Type is a required value");

      if (
         !payload.customerId ||
         _.isEmpty(payload.customerId) ||
         payload.customerId === "0"
      )
         errors = errors.concat("<br />Customer Name is required");
      if (!payload.invoiceNumber || _.isEmpty(payload.invoiceNumber))
         errors = errors.concat("<br />Invoice Numbers is required");
      if (!payload.productType || _.isEmpty(payload.productType))
         errors = errors.concat("<br />Customer Part Number is required");
      if (!payload.caseNumber || _.isEmpty(payload.caseNumber))
         errors = errors.concat("<br />Case Number is required");
      if (!payload.targetDate)
         errors = errors.concat(
            "<br />Target Completion Date is a required value"
         );
      if (payload.quantity < 0)
         errors = errors.concat("<br />Quantity is required");
      // if(!payload.followUpBy) errors = errors.concat("<br />Follow up by is a required value");
      if (!payload.problemDescription)
         errors = errors.concat("<br />Problem Description is a required value");
      if (payload.status !== 1) {
         if (!payload.rootCause || _.isEmpty(payload.rootCause))
            errors = errors.concat(
               "<br />Root Cause is required to mark the action complete or canceled"
            );
         if (!payload.action || _.isEmpty(payload.action))
            errors = errors.concat(
               "<br />Corrective Action Taken is required to mark the action complete or canceled"
            );
      }
      if (_.isEmpty(errors)) return true;
      toast.error(errors);
      return false;
   };

   return (
      <DesktopContainer screenName={""}>
         <ServiceThreadSpinner on={isLoading}>
            <form>
               <fieldset disabled={isDisabled}>
                  <div className="filter-header">
                     <Row className="newCorrectiveAction">
                        <Col xs={3} />
                        <Col xs={6} className="text-white">
                           <h5>
                              {currentActionDto.displayId
                                 ? currentActionDto.displayId
                                 : "Determined upon save"}
                           </h5>
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Originator" />
                        <Col xs={6} className="text-white">
                           <h5>{currentActionDto.originator}</h5>
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Created Date" />
                        <Col xs={6} className="text-white">
                           <h5>
                              {dateHelpers.toLongDateString(currentActionDto.createdDate)}
                           </h5>
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Target Completion Date" />
                        <Col xs={6} className="text-white">
                           <DebounceInput
                              type="date"
                              onChange={(e) => onChange("targetDate", e.target.value)}
                              value={currentActionDto.targetDate}
                              debounceTimeout={500}
                              className={"px-2"}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Target Follow-up Date" />
                        <Col xs={6} className="text-white">
                           <DebounceInput
                              type="date"
                              onChange={(e) =>
                                 onChange("targetFollowUpDate", e.target.value)
                              }
                              value={currentActionDto.targetFollowUpDate}
                              debounceTimeout={500}
                              className={"px-2"}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Customer Name" />
                        <Col xs={6}>
                           <CustomerTypeahead
                              value={returnCustomerSelect(
                                 currentActionDto.customerIdSelect
                              )}
                              onChange={(opt) => onChange("customerIdSelect", opt)}
                              disabled={isDisabled}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Invoice Numbers" />
                        <Col xs={6}>
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={selectList.invoices}
                              value={returnInvoiceTypeSelect(
                                 currentActionDto.invoiceNumber,
                                 currentActionDto.invoiceNumberSelect
                              )}
                              onChange={(opt) => onChange("invoiceNumberSelect", opt)}
                              placeholder={"Please select a customer above..."}
                              isMulti
                              isClearable={false}
                              isDisabled={selectLoading || isDisabled}
                           />
                        </Col>
                        <Col
                           className={"p-0"}
                           children={
                              <ServiceThreadSpinner
                                 left
                                 white
                                 small
                                 on={selectLoading}
                                 children={null}
                              />
                           }
                        />
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Customer Item #" />
                        <Col xs={6}>
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={selectList.partNumbers}
                              placeholder={"Please select one or more invoices above..."}
                              value={currentActionDto.productTypeSelect}
                              onChange={(opt) => onChange("productTypeSelect", opt)}
                              isDisabled={selectLoading || isDisabled}
                           />
                        </Col>
                        {!selectLoading && itemUofM ? (
                           <Col xs="1" className="text-white">
                              <h5 style={{ textAlign: "left", alignContent: "center" }}>
                                 {itemUofM}
                              </h5>
                           </Col>
                        ) : null}
                        <Col
                           className={"p-0"}
                           children={
                              <ServiceThreadSpinner
                                 left
                                 white
                                 small
                                 on={selectLoading}
                                 children={null}
                              />
                           }
                        />
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Case Numbers" />
                        <Col xs={6}>
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={selectList.caseNumbers}
                              value={currentActionDto.caseNumberSelect}
                              placeholder={"Please select an item above..."}
                              onChange={(opt) => onChange("caseNumberSelect", opt)}
                              isMulti
                              isClearable={false}
                              isDisabled={selectLoading || isDisabled}
                           />
                        </Col>
                        <Col
                           className={"p-0"}
                           children={
                              <ServiceThreadSpinner
                                 left
                                 white
                                 small
                                 on={selectLoading}
                                 children={null}
                              />
                           }
                        />
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Quantity" />
                        <Col xs={6} className="text-white">
                           <Input
                              type="number"
                              value={currentActionDto.quantity}
                              onChange={(e) => onChange("quantity", e.target.value)}
                              placeholder={"Enter Quantity..."}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Problem Type" />
                        <Col xs={6}>
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={staticSelectList.problemTypes}
                              value={currentActionDto.problemTypeIdSelect}
                              onChange={(opt) => onChange("problemTypeIdSelect", opt)}
                              isDisabled={isDisabled}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Production Operator" />
                        <Col xs={6} className="text-white">
                           <Input
                              type="text"
                              value={currentActionDto.productionOperator}
                              onChange={(e) =>
                                 onChange("productionOperator", e.target.value)
                              }
                              placeholder={"Enter Operator..."}
                           />
                        </Col>
                     </Row>
                     <Row
                        children={
                           <Col
                              xs={{ size: 6, offset: 3 }}
                              className="text-white"
                              children={"Problem Description:"}
                           />
                        }
                     />
                     <Row className="newCorrectiveAction">
                        <Col xs={{ size: 6, offset: 3 }} className="text-white">
                           <Input
                              className="correctiveTB serviceThreadTextArea"
                              type="textarea"
                              value={currentActionDto.problemDescription}
                              maxLength={255}
                              style={{ height: "150px", marginTop: "4px" }}
                              onChange={(e) =>
                                 onChange("problemDescription", e.target.value)
                              }
                              placeholder={"Enter Description..."}
                           />
                        </Col>
                     </Row>
                     <Row
                        children={
                           <Col
                              xs={{ size: 6, offset: 3 }}
                              className="text-white"
                              children={
                                 "Investigate and Determine Root Cause of Problem:"
                              }
                           />
                        }
                     />
                     <Row className="newCorrectiveAction">
                        <Col xs={{ size: 6, offset: 3 }} className="text-white">
                           <Input
                              className="correctiveTB serviceThreadTextArea"
                              type="textarea"
                              value={currentActionDto.rootCause}
                              maxLength={255}
                              style={{ height: "150px", marginTop: "4px" }}
                              onChange={(e) => onChange("rootCause", e.target.value)}
                              placeholder={"Enter Description..."}
                           />
                        </Col>
                     </Row>
                     <Row
                        children={
                           <Col
                              xs={{ size: 6, offset: 3 }}
                              className="text-white"
                              children={"Corrective Action Taken:"}
                           />
                        }
                     />
                     <Row className="newCorrectiveAction">
                        <Col xs={{ size: 6, offset: 3 }} className="text-white">
                           <Input
                              type="textarea"
                              className="correctiveTB serviceThreadTextArea"
                              value={currentActionDto.action}
                              maxLength={255}
                              style={{ height: "150px", marginTop: "4px" }}
                              onChange={(e) => onChange("action", e.target.value)}
                              placeholder={"Enter Description..."}
                           />
                        </Col>
                     </Row>
                     <Row className="newProductionItemLine">
                        <Col xs="3" />
                        <RadioOption
                           classType={status.Closed}
                           currentValue={currentActionDto.status}
                           onChange={() =>
                              onChangeStatus("status", status.Closed?.value)
                           }
                           fieldName="correctiveStatus"
                        />
                        <RadioOption
                           classType={status.Canceled}
                           currentValue={currentActionDto.status}
                           onChange={() =>
                              onChangeStatus("status", status.Canceled?.value)
                           }
                           fieldName="correctiveStatus"
                        />
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Completed By" />
                        <Col xs={6} className="text-white">
                           <h5>{currentActionDto.completedBy}</h5>
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Completion Date" />
                        <Col xs={6} className="text-white">
                           <h5>
                              {currentActionDto.completionDate
                                 ? dateHelpers.toLongDateString(
                                    currentActionDto.completionDate
                                 )
                                 : null}
                           </h5>
                        </Col>
                     </Row>
                     <hr />
                     <Row
                        children={
                           <Col
                              xs={{ size: 6, offset: 3 }}
                              className="text-white"
                              children={"Follow-up Comments:"}
                           />
                        }
                     />
                     <Row className="newCorrectiveAction">
                        <Col xs={{ size: 6, offset: 3 }} className="text-white">
                           <Input
                              type="textarea"
                              className="correctiveTB serviceThreadTextArea"
                              value={currentActionDto.followUp}
                              maxLength={255}
                              style={{ height: "150px", marginTop: "4px" }}
                              onChange={(e) => onChange("followUp", e.target.value)}
                              placeholder={"Enter Description..."}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Follow-up By" />
                        <Col xs={6}>
                           <Select
                              classNamePrefix="serviceThreadSelect"
                              options={staticSelectList.followByUsers}
                              value={currentActionDto.followUpBySelect}
                              onChange={(opt) => onChange("followUpBySelect", opt)}
                              isDisabled={isDisabled}
                           />
                        </Col>
                     </Row>
                     <Row className="newCorrectiveAction">
                        <ColPrompt title="Follow-up Date" />
                        <Col xs={6} className="text-white">
                           <DebounceInput
                              type="date"
                              onChange={(e) => onChange("followUpDate", e.target.value)}
                              value={dateHelpers.formatDateForServer(
                                 currentActionDto.followUpDate
                              )}
                              debounceTimeout={500}
                           />
                        </Col>
                     </Row>
                     <hr />
                     <Row>
                        <Col xxl={{ size: 8, offset: 2 }}>
                           <Card className="dashboard-card">
                              <CardBody>
                                 <AttachmentManager
                                    attachmentTypeId={
                                       constants.ATTACHMENT_TYPES.CorrectiveAction
                                    }
                                    contextName={currentActionDto.displayId}
                                    attachmentKey={currentActionDto.displayId}
                                 />
                              </CardBody>
                           </Card>
                        </Col>
                     </Row>
                     <Row>
                        <div className="inline d-flex justify-content-center">
                           <Button
                              className="shredButton"
                              children={"Save Changes"}
                              onClick={() => SaveCorrectiveAction()}
                           />
                           <Button
                              className="shredButton mx-5"
                              children={"Print Report"}
                              onClick={() => runReport()}
                           />
                        </div>
                     </Row>
                  </div>
               </fieldset>
            </form>
            <Row className="mt-3">
               <div className="inline d-flex justify-content-center">
                  <Button
                     className="shredButton"
                     children={"Cancel Return To Search"}
                     onClick={() =>
                        navigate(constants.ENDPOINTS.correctiveActionSearch)
                     }
                  />
               </div>
            </Row>
         </ServiceThreadSpinner>
      </DesktopContainer>
   );
}
